import React, { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DocumentModalSkeleton } from "@/components/skeletons";
import { Document, Page } from "react-pdf";

interface DocumentModalContent {
    title: string;
    content: string;
}

interface DocumentModalProps {
    show?: boolean;
    onHide?: () => void;
    content: DocumentModalContent | null;
}

const DocumentModal = ({ show, onHide, content }: DocumentModalProps) => {
    const [numPages, setNumPages] = useState(0);
    const [documentReady, setDocumentReady] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<string>("");

    useEffect(() => {
        setNumPages(0);
        setDocumentReady(false);
        setDocumentUrl("");

        if (!show || !content?.content) return;

        const controller = new AbortController();
        let objectUrl = "";

        const loadDocument = async () => {
            try {
                const response = await fetch(content.content, { signal: controller.signal });
                const blob = await response.blob();
                objectUrl = URL.createObjectURL(blob);
                setDocumentUrl(objectUrl);
            } catch {
                if (!controller.signal.aborted) {
                    setDocumentUrl(content.content);
                }
            }
        };

        loadDocument();

        return () => {
            controller.abort();
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [show, content?.content]);

    const handleClose = () => onHide?.();

    return (
        <Dialog
            open={show}
            onOpenChange={(open) => {
                if (!open) handleClose();
            }}
        >
            <DialogContent className="gs-document-dialog sm:max-w-4xl">
                {!content ? (
                    <DocumentModalSkeleton />
                ) : (
                    <>
                        <div className="gs-document-dialog__header">
                            <h4 className="gs-document-dialog__title">{content.title}</h4>
                        </div>

                        <div className="gs-document-dialog__body">
                            {!documentReady ? (
                                <div className="gs-document-dialog__loading">
                                    <DocumentModalSkeleton bare />
                                </div>
                            ) : null}
                            {documentUrl ? (
                                <Document
                                    file={documentUrl}
                                    onLoadSuccess={({ numPages: loadedPages }) => {
                                        setNumPages(loadedPages);
                                        setDocumentReady(true);
                                    }}
                                    onLoadError={() => setDocumentReady(true)}
                                    loading=""
                                >
                                    <div className="gs-document-dialog__pages">
                                        {Array.from({ length: numPages }, (_, index) => (
                                            <Page
                                                key={`page_${index + 1}`}
                                                pageNumber={index + 1}
                                                className="gs-document-dialog__page"
                                                renderTextLayer={false}
                                                renderAnnotationLayer={false}
                                            />
                                        ))}
                                    </div>
                                </Document>
                            ) : null}
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default DocumentModal;
